/**
 * Sidebar navigation data — COORD-23: ROS Shell Wrap.
 *
 * Icons use @phosphor-icons/react. getItemIcon / getSectionIcon return a
 * Phosphor component reference (or null). NavIcon in Layout.jsx renders it.
 */

import {
  // Section icons
  Sparkle, TrendUp, Users, Globe, ChartLine, Wrench, Gear,
  ListBullets, NotePencil, Upload, ShieldCheck,
  // Nav item icons
  ChatCircleDots, CalendarCheck, UsersThree, SquaresFour, FileText,
  Crosshair, PaperPlaneTilt, ShieldWarning, Kanban, AddressBook, Buildings,
  Star, Bank, Compass, ChartLineUp, Newspaper, Scroll, Rss,
  SlidersHorizontal, ClipboardText, Cube, ChartPie, CurrencyDollar,
  ArrowsLeftRight, Receipt, CalendarBlank, CheckCircle, ChartBar,
  Medal, GitBranch, BookOpen, Bell, Database, Key, Tray,
  DownloadSimple, CloudArrowDown, Clock, ClockCounterClockwise, Terminal, Desktop, CheckSquare,
  DesktopTower, Robot, Plus, CopySimple, PlayCircle, Truck, ShieldStar,
  FolderOpen, Lightning, MagnifyingGlass, Question, Info,
  ShareNetwork, Microphone, House, MagicWand, Scales, Megaphone,
  Certificate, Cpu, Ticket, Stack, Rocket, ListChecks,
} from "@phosphor-icons/react";
import {
  TA_ATTENDANCE_SIDEBAR_PERMS,
  TA_TIME_SIDEBAR_PERMS,
  timeHubSidebarPath,
} from "../utils/taAdminAccess";

// ── Section icon map (sectionId → Phosphor component) ────────────────
const SECTION_ICON_MAP = {
  ai_hub:       Sparkle,
  growth:       TrendUp,
  people:       Users,
  people_admin: ShieldCheck,
  intelligence: Globe,
  operations:   ChartLine,
  iso:          ShieldCheck,
  workflows:    ListBullets,
  agents:       Wrench,
  content:      NotePencil,
  sources:      Upload,
  admin:        Gear,
};

// ── Item icon map (route → Phosphor component) ────────────────────────
const ITEM_ICON_MAP = {
  // AI Hub
  "/hub":                               ChatCircleDots,
  "/hub/transcript-tool":               Microphone,
  "/hub/weekly-focus":                  CalendarCheck,
  "/hub/weekly-focus/team":             UsersThree,
  "/hub/templates":                     SquaresFour,
  "/hub/documents":                     FileText,
  "/feedback":                          ChatCircleDots,
  "/helpdesk":                          Ticket,

  // Growth
  "/pipeline":                          Kanban,
  "/leads":                             Kanban,
  "/screening":                         Crosshair,
  "/lead-triage":                       Crosshair,
  "/triage":                            Crosshair,
  "/crm/pulse":                         Star,
  "/opportunities":                     Kanban,
  "/crm/contacts":                      AddressBook,
  "/crm/organizations":                 Buildings,
  "/past-performance":                  Star,
  "/growth/marketing-responses":        PaperPlaneTilt,
  "/growth/marketing-responses/stats":  PaperPlaneTilt,
  "/growth/marketing-responses/routing":PaperPlaneTilt,
  "/growth/rd-responses":               FileText,
  "/growth/unsolicited-outreach":        Megaphone,
  "/growth/rd-president-queue":         ShieldCheck,
  "/intel/watchdog":                    ShieldWarning,
  "/staffing":                          UsersThree,
  "/staffing/admin":                    Gear,
  "/upload":                            Upload,
  "/sam":                               Bank,
  "/proactive":                         Compass,
  "/forecast":                          ChartLineUp,

  // Intelligence
  "/intel":                             SquaresFour,
  "/intel/brief":                       Newspaper,
  "/intel/brief-v2":                    Newspaper,
  "/intel/morning-brief-v2":            Newspaper,
  "/intel/brief/archive":               Newspaper,
  "/intel/frontier":                    Sparkle,
  "/intel/frontier-v2":                 Sparkle,
  "/intel/daily-ai-v2":                 Sparkle,
  "/intel/daily-frontier-ai":           Sparkle,
  "/intel/weekly-reports":              Scroll,
  "/intel/weekly-v2":                   Scroll,
  "/intel/v2":                          Cube,
  "/intel/v2/audit":                    ClipboardText,
  "/intel/stream":                      Rss,
  "/intel/constellation":               ShareNetwork,
  "/intel/key-talent":                  Users,
  "/intel/actions":                     Lightning,
  "/intel/search":                      MagnifyingGlass,
  "/intel/admin":                       SlidersHorizontal,
  "/intel/dashboard":                   ChartBar,
  "/intel/speakers":                    Microphone,
  "/intel/events":                      CalendarBlank,
  "/intel/videos":                      PlayCircle,
  "/intel/feeds":                       BookOpen,
  "/intel/ma-radar":                    ChartBar,
  "/intel/discovery":                   Compass,
  "/intel/discovery/admin":             SlidersHorizontal,

  // Content
  "/content/brief":                     Newspaper,
  "/content/drafts":                    NotePencil,
  "/content/analytics":                 ChartBar,
  "/content/settings":                  Gear,

  // // Operations (Ops/Revenue — dormant, not yet linked in sidebar)
  // "/ops/dashboard":                     ChartPie,
  // "/ops/projects":                      Buildings,
  // "/ops/workforce":                     Users,
  // "/ops/profitability":                 CurrencyDollar,
  // "/ops/forecast":                      ChartLineUp,
  // "/ops/allocation":                    ArrowsLeftRight,
  // "/ops/invoices":                      Receipt,
  // "/ops/schedule":                      CalendarBlank,

  // Operations — ISO module (F0-2)
  "/iso":                               FileText,
  "/iso/documents":                     FileText,
  "/iso/non-conformities":              ShieldWarning,
  "/iso/risks":                         ShieldStar,
  "/iso/action-plans":                  Kanban,
  "/iso/search":                        MagnifyingGlass,
  // "/iso/requests":                      FileText, // Document Requests page no longer used
  "/iso/coding-wizard":                 MagicWand,
  "/iso/control-center":                ChartPie,
  "/iso/clauses":                       Stack,
  "/iso/access-control":                Key,

  // People
  "/people":                            Users,
  "/people/certifications":             Certificate,
  "/people/rro":                        ClipboardText,
  "/people/voice-biography":            Microphone,
  "/people/actionables":                ListBullets,
  "/people/aars":                       ClipboardText,
  "/people/journal":                    NotePencil,
  "/people/todos":                      CheckSquare,
  "/people/priorities":                 Star,
  "/people/checkin":                    CheckCircle,
  "/people/team-reviews":               UsersThree,
  "/people/evaluations":                ChartBar,
  "/people/evaluations/ranking":        Medal,
  "/people/my-pap":                     Rocket,
  "/people/team-paps":                  ListChecks,
  "/people/review-admin":               CalendarCheck,
  "/people/reports":                    ChartBar,
  "/people/my-day":                     House,
  "/people/shifts":                     CalendarBlank,
  "/people/leave":                      CalendarBlank,
  "/people/time":                       Clock,
  "/people/approvals":                  Tray,
  "/notepad":                           NotePencil,

  // Timekeeping (module off — kept for when re-enabled)
  "/people/timesheet":                  Clock,
  "/people/timesheet/approve":          CheckCircle,
  "/people/timesheet/pto":              CalendarBlank,
  "/people/timesheet/pto-calendar":     CalendarBlank,
  "/people/timesheet/forecast":         ChartLineUp,
  "/people/timesheet/charge-codes":     ChartBar,
  "/people/timesheet/compliance":       ShieldCheck,
  "/people/timesheet/actuals":          ChartBar,
  "/people/timesheet/holidays":         Star,
  "/people/timesheet/zoho-leave":        CloudArrowDown,
  "/people/timesheet/admin/balances":    Scales,
  "/people/timesheet/admin/leave":       CalendarCheck,
  "/people/timesheet/admin/timesheets":  ClipboardText,
  "/people/timesheet/admin/control":     ChartBar,
  "/people/timesheet/admin/reports":     FileText,
  "/people/timesheet/admin/attendance": CalendarBlank,
  "/people/timesheet/admin/audit":       Scroll,
  "/people/timesheet/policies":         ShieldCheck,
  "/people/timesheet/audit":            MagnifyingGlass,
  "/people/timesheet/bulk-import":      Upload,
  "/people/timesheet/policies":         ShieldCheck,
  "/people/timesheet/handover":         ArrowsLeftRight,
  "/people/timesheet/clerk-entry":      Users,
  "/people/timesheet/reconciliation":   ChartBar,

  // Builder / Agents
  "/builder/processes":                 FolderOpen,
  "/builder/disposition":               Kanban,
  "/builder/discovery":                 Compass,
  "/builder/compliance":                CheckSquare,
  "/builder/specs":                     FileText,
  "/builder/actions":                   Lightning,
  "/builder/tasks":                     ListBullets,
  "/builder/policy":                    ShieldCheck,
  "/builder/audit":                     MagnifyingGlass,
  "/agents":                            Robot,
  "/agents/new":                        Plus,
  "/agents/templates":                  CopySimple,
  "/agents/runs":                       PlayCircle,
  "/contract-vehicles":                 Truck,
  "/contract-vehicles/shield":          ShieldStar,

  // Workflows (module off — kept for when re-enabled)
  "/workflows/tasks":                   CheckSquare,
  "/workflows/templates":               CopySimple,
  "/workflows/runs":                    PlayCircle,
  "/workflows/schedules":               Clock,
  "/workflows/reports":                 ChartBar,
  "/workflows/agents":                  Robot,

  // Admin
  "/ops/system":                        DesktopTower,
  "/admin/task-monitor":                Desktop,
  "/admin/focus-compliance":            CheckSquare,
  "/settings/platform":                 SlidersHorizontal,
  "/settings/notifications":            Bell,
  "/settings/services":                 Cpu,
  "/settings/evolution":                GitBranch,
  "/settings/corpus":                   BookOpen,
  "/settings/users":                    Users,
  "/settings/roles":                    ShieldCheck,
  "/settings/data":                     Database,
  "/settings/api-keys":                 Key,
  "/settings/inbound-log":              Tray,
  "/crm/import":                        DownloadSimple,
  "/sam/bulk":                          Clock,
  "/highergov/bulk":                    CloudArrowDown,
  "/settings/feedback":                 ChatCircleDots,
  "/activity":                          ClockCounterClockwise,
  "/activity/usage":                    ChartLine,
  "/admin/system-log":                  Terminal,
  "/coord/tasks":                       CheckSquare,

  // Footer
  "/help":  Question,
  "/about": Info,
};

/** Returns the Phosphor icon component for a nav item route, or null. */
export function getItemIcon(route) {
  return ITEM_ICON_MAP[route] ?? null;
}

/** Returns the Phosphor icon component for a section, or null. */
export function getSectionIcon(sectionId) {
  return SECTION_ICON_MAP[sectionId] ?? null;
}

export const SIDEBAR_SECTIONS = [
  {
    id: "ai_hub",
    label: "AI Hub",
    defaultRoute: "/hub",
    featureFlag: "module.ai_hub",
    items: [
      { to: "/hub", label: "Chat", featureFlag: "module.ai_hub", permission: "ai_hub.view" },
      { to: "/hub/transcript-tool", label: "Transcript Tool", featureFlag: "module.ai_hub", permission: "ai_hub.view", tooltip: "Record or upload audio, edit the transcript, download as DOCX/TXT" },
      { to: "/hub/weekly-focus", label: "Focus Notes", featureFlag: "module.ai_hub", permission: "ai_hub.view" },
      { to: "/hub/weekly-focus/team", label: "Team Notes", featureFlag: "module.ai_hub", permission: "ai_hub.team_notes" },
      // { to: "/hub/templates", label: "Templates", featureFlag: "module.ai_hub", adminOnly: true },
      // { to: "/hub/documents", label: "Documents", featureFlag: "module.ai_hub", adminOnly: true },
      { to: "/feedback", label: "Feedback", featureFlag: "module.feedback", permission: "feedback.view_own", tooltip: "Your submitted bug reports, feature requests, and automation requests"},
      { to: "/helpdesk", label: "Helpdesk", featureFlag: "module.helpdesk", permission: "helpdesk.view", tooltip: "Submit and track IT tickets. IT staff update tickets in ITMS; you see status here."},
    ],
  },
  {
    id: "growth",
    label: "Growth",
    defaultRoute: "/pipeline",
    featureFlag: "module.growth",
    items: [
      { to: "/triage", label: "Lead Triage", permission: "leads.manual_screen_1", tooltip: "Unified triage queue — score, enrich, disposition. Triage / All Leads tabs." },
      { to: "/growth/marketing-responses", label: "Marketing Responses", permission: "marketing.responses.view", featureFlag: "module.marketing_responses", tooltip: "SS/RFI auto-response workflow — Board / Stats / Routing tabs" },
      { to: "/growth/rd-responses", label: "R&D Responses", permission: "rd.view", tooltip: "Federal R&D solicitation response workflow — Board + Final Review tabs" },
      { to: "/growth/unsolicited-outreach", label: "Unsolicited Outreach", permission: "leads.manual_screen_1", tooltip: "Leads flagged for proactive customer outreach — no formal solicitation yet. Promote to pipeline or close." },
      { to: "/pipeline", label: "CRM Pipeline", permission: "leads.view", featureFlag: "module.crm" },
      { to: "/intel/watchdog", label: "Watchdog", permission: "leads.view", tooltip: "Opportunity Watchdog Agent — daily sanity-check layer (ingestion integrity / HigherGov cross-check / watched-pursuit lifecycle / rejection review) with disposition feedback loop. WATCHDOG-1 feature-complete; watchdog.enabled stays off until operator turn-on." },
      { to: "/crm/contacts", label: "Contacts", permission: "leads.view", featureFlag: "module.crm" },
      { to: "/crm/organizations", label: "Organizations", permission: "leads.view", featureFlag: "module.crm" },
      { to: "/past-performance", label: "Past Performance", permission: "leads.view", featureFlag: "module.past_performance" },
      { subHeader: "Sources" },
      { to: "/upload", label: "Upload", permission: "leads.view" },
      { to: "/sam", label: "SAM.gov", permission: "leads.view", featureFlag: "integration.sam_gov" },
      { to: "/staffing", label: "Staffing Leads", permission: "leads.view", featureFlag: "module.staffing" },
      { to: "/staffing/admin", label: "Staffing Leads Settings", permission: "admin.settings", featureFlag: "module.staffing" },
      { to: "/proactive", label: "Discovery Agent", permission: "leads.view", featureFlag: "module.proactive" },
      { to: "/forecast", label: "Agency Forecasts", permission: "leads.view", featureFlag: "module.forecast" },
      { to: "/coord/tasks", label: "My Tasks", permission: "coord.view_own_tasks" },
      { to: "/contract-vehicles", label: "Contract Vehicles", permission: "contract_vehicles.view" },
    ],
  },
  {
    id: "intelligence",
    label: "Intelligence",
    defaultRoute: "/intel/dashboard",
    featureFlag: "module.osint_lake",
    items: [
      { to: "/intel/dashboard", label: "Intel Dashboard", permission: "intel.view", tooltip: "Is Intel healthy today? Morning Brief completeness per section, news processing status, failing or silent sources, alerts and a 14-day story trend." },
      { to: "/intel/brief-v2", label: "Morning Brief", permission: "intel.view", featureFlag: "module.morning_brief_v2", tooltip: "Daily intelligence brief — BLUF + five sections (customers, competi-mates, OEMs, market, LATAM), with audio. Consolidated surface; replaces the V1 brief and the Exec CO-brief." },
      { to: "/intel/daily-frontier-ai", label: "Daily Frontier AI Brief", permission: "intel.view", featureFlag: "module.intel_v2_products", tooltip: "Long-form daily essay on the state of frontier AI — frontier labs (Anthropic / OpenAI / DeepMind / Meta / Mistral / xAI), open-weight models (Llama / Qwen / DeepSeek / Gemma), enterprise AI adoption + novel uses, AI/ML company moves, research. Federal/DoD an afterthought. Opus over V2 L2 analyses with audio. Consolidated surface; replaces V1 AI Frontier Brief, V2 AI Frontier Brief, and V1 Daily AI Report." },
      { to: "/intel", label: "Intel Fusion Center", permission: "intel.view", tooltip: "9-category intelligence dashboard — master-detail layout with Sonnet summaries and source-type chips, refreshed every 2h" },
      { to: "/intel/weekly-v2", label: "Weekly Report", permission: "intel.view", featureFlag: "module.intel_v2_products", tooltip: "V2 sibling of Weekly Reports — adds thread evolution (claims that strengthened / weakened across the week) and conflict surfacing (where sources disagreed) on top of the Bloomberg-style long-form recap. Topic-filtered (ILIKE) or all-topics, configurable window." },
      // { to: "/intel/weekly-reports", label: "Weekly Reports", permission: "intel.view", tooltip: "Deep 2000-4000 word analyst reports across 11 topics — compiled Saturday 06:00 CT" },
      { to: "/intel/admin", label: "Intel Admin", permission: "intel.configure", tooltip: "Source management, collection settings. Intel V2 tab inside houses Morning Brief V2 and V2 Fusion Intel launchers." },
      { to: "/intel/stream", label: "Intel Stream", permission: "intel.view", tooltip: "Raw collection stream + Conviction / Events / Videos tabs" },
      { to: "/intel/v2/audit", label: "V2 Quality Audit", permission: "admin.settings", featureFlag: "module.intel_v2_products", tooltip: "Phase 1 quality audit workflow — sample L1 / L2 outputs one at a time, rate against the source material on 4 binary criteria (faithful / complete / schema-compliant / hallucinations), notes optional. Sidebar shows rolling aggregates + by-type breakdown. Phase 1 exit: 50 ratings per content_type on L1, 50 per analysis_type on L2." },
      { to: "/settings/corpus", label: "Reference Corpus", permission: "corpus.view", featureFlag: "platform.reference_corpus" },
    ],
  },
  {
    // Ops/Revenue — dormant, not yet linked in sidebar. Routes already live
    // in main.jsx (OPS-1 / OPS-1B); re-add here as items when relinked.
    // Kept as its own section (distinct id from "iso" below) so it doesn't
    // collide with the real Operations section that already exists in prod.
    id: "operations",
    label: "Operations",
    items: [
      // { to: "/ops/dashboard", label: "Dashboard", permission: "ops.view" },
      // { to: "/ops/projects", label: "Projects", permission: "revenue.view" },
      // { to: "/ops/workforce", label: "Workforce Planning", permission: "ops.view", featureFlag: "module.ops" },
      // { to: "/ops/profitability", label: "Profitability", permission: "ops.view", featureFlag: "module.ops" },
      // { to: "/ops/forecast", label: "Forecast", permission: "revenue.view" },
      // { to: "/ops/allocation", label: "Allocation", permission: "revenue.view" },
      // { to: "/ops/invoices", label: "Invoices", permission: "revenue.billing" },
      // { to: "/ops/schedule", label: "Schedule", permission: "revenue.view" },
    ],
  },
  {
    id: "iso",
    label: "ISO",
    defaultRoute: "/iso/documents",
    // ISO module (F0-2) — stub items, pages ship in D1/D3/etc.
    items: [
      { to: "/iso/documents", label: "Document Matrix", permission: "iso.view", permissionAny: ["iso.documents.view", "iso.requests.create"], featureFlag: "module.iso", tooltip: "ISO Master Document Matrix — controlled documents registry" },
      // Hidden for now — not part of the document management flow (Laura/Julia testing). Still routable, just not shown in the sidebar.
      { to: "/iso/non-conformities", label: "Non-Conformities", permission: "iso.view", featureFlag: "module.iso", hidden: true },
      { to: "/iso/risks", label: "Risk Matrix", permission: "iso.view", featureFlag: "module.iso", tooltip: "Structured repository of organizational risks, linked to Action Plans", hidden: true },
      { to: "/iso/action-plans", label: "Action Plans", permission: "iso.view", featureFlag: "module.iso", tooltip: "Unified corrective/preventive action hub across all ISO processes", hidden: true },
      { to: "/iso/search", label: "Search Center", permission: "iso.view", permissionAny: ["iso.documents.view"], featureFlag: "module.iso", tooltip: "Search the document matrix with saved filters and pagination", hidden: true },
      // Document Requests page no longer used:
      // { to: "/iso/requests", label: "Document Requests", permission: "iso.view", permissionAny: ["iso.requests.create"], featureFlag: "module.iso", tooltip: "Submit and track document requests", hidden: true },
      { to: "/iso/coding-wizard", label: "Coding Wizard", permission: "iso.edit", featureFlag: "module.iso", tooltip: "Suggest next document code from the existing Document Matrix (org standard)" },
      { to: "/iso/control-center", label: "Control Center", permission: "iso.view", featureFlag: "module.iso" },
      { to: "/iso/clauses", label: "Clauses", permission: "iso.manage", featureFlag: "module.iso", tooltip: "Standard → Clause → Subclause registry, used to tag which clauses a document supports", hidden: true },
      { to: "/iso/access-control", label: "Risk & Access Control", permission: "iso.admin", featureFlag: "module.iso", tooltip: "Requires the iso.admin permission — Risk Matrix overview + iso.* permission matrix by role", hidden: true },
    ],
  },
  {
    id: "people",
    label: "People",
    defaultRoute: "/people",
    featureFlag: "module.people",
    items: [
      { to: "/people", label: "Personnel", permission: "people.view" },
      { to: "/people/certifications", label: "Certifications", permission: "people.view" },
      { to: "/people/rro", label: "Roles & Responsibilities", permission: "people.view" },
      { to: "/people/actionables", label: "Daily Actionables", hidden: true },
      { to: "/people/voice-biography", label: "Voice Biography", permission: "people.biography.edit", featureFlag: "module.voice_biography", tooltip: "Record a short voice interview to build your professional biography" },
      { to: "/people/priorities", label: "Company Priorities", permission: "people.view", featureFlag: "module.cpm", hidden: true },
      { subHeader: "Time & Attendance" },
      { to: "/people/my-day", label: "Dashboard", permission: "timekeeping.module", tooltip: "Your leave, time, and attendance overview" },
      { to: "/people/shifts", label: "Daily Attendance", permission: "timekeeping.module", jurisdiction: "CO", tooltip: "Colombia only — set daily work status; approved leave shows as PTO" },
      { to: "/people/leave", label: "Leave", permission: "timekeeping.module", tooltip: "Request time off and view balances" },
      { to: "/people/time", label: "Time", permission: "timekeeping.module", tooltip: "Daily and weekly time entry" },
      { to: "/people/approvals", label: "Approvals", permission: "timekeeping.approve", tooltip: "Approve leave and timesheet requests for your team" },
      { subHeader: "Personal" },
      { to: "/notepad", label: "Notepad", tooltip: "Combined To Do + Journal", hidden: true },
      { subHeader: "Performance" },
      { to: "/people/checkin", label: "My Check-In", permission: "rro.view", featureFlag: "module.cpm" },
      { to: "/people/my-pap", label: "My PAP", permission: "rro.view", featureFlag: "module.cpm", tooltip: "Your own Performance Acceleration Plan, if you have one" },
      { to: "/people/team-reviews", label: "Team Reviews", matchPaths: ["/people/team-reviews", "/people/team-checkins", "/people/evaluations"], excludePaths: ["/people/evaluations/ranking"], permission: "rro.manage_team", managerOnly: true, featureFlag: "module.cpm", tooltip: "Check-in reviews and evaluations for your direct reports" },
      { to: "/people/evaluations/ranking", label: "Forced Ranking", permission: "rro.manage_team", managerOnly: true, featureFlag: "module.cpm" },
      { to: "/people/team-paps", label: "Team PAPs", permission: "rro.manage_team", managerOnly: true, featureFlag: "module.cpm", tooltip: "Performance Acceleration Plans you're running for your direct reports" },
      // ── Timekeeping — hidden until module is ready ──────────────────
      // { subHeader: "Timekeeping" },
      // { to: "/people/timesheet", label: "My Timesheet", featureFlag: "module.timekeeping" },
      // { to: "/people/timesheet/approve", label: "Approve Timesheets", permission: "timekeeping.approve", featureFlag: "module.timekeeping" },
      // { to: "/people/timesheet/pto", label: "My PTO", featureFlag: "module.timekeeping" },
      // { to: "/people/timesheet/pto-calendar", label: "PTO Calendar", permission: "pto.approve", featureFlag: "module.timekeeping" },
      // { to: "/people/timesheet/forecast", label: "My Forecast", featureFlag: "module.timekeeping" },
      // { to: "/people/timesheet/charge-codes", label: "Charge Codes", permission: "timekeeping.admin", featureFlag: "module.timekeeping" },
      // { to: "/people/timesheet/compliance", label: "Compliance", permission: "timekeeping.reports", featureFlag: "module.timekeeping" },
      // { to: "/people/timesheet/actuals", label: "Actuals vs Planned", permission: "timekeeping.reports", featureFlag: "module.timekeeping" },
      // { to: "/people/timesheet/holidays", label: "Holidays", permission: "timekeeping.admin", featureFlag: "module.timekeeping" },
      // { to: "/people/timesheet/audit", label: "Audit Trail", permission: "timekeeping.admin", featureFlag: "module.timekeeping" },
      // { to: "/people/timesheet/bulk-import", label: "Bulk Time Import", permission: "timekeeping.manage", featureFlag: "module.timekeeping" },
      // ── PTO Admin — hidden until module is ready ─────────────────────
      // { subHeader: "PTO Admin" },
      // { to: "/people/timesheet/policies", label: "PTO Policies", permission: "pto.policy.manage", featureFlag: "module.timekeeping.pto_policies" },
      // { to: "/people/timesheet/handover", label: "Handover Plans", featureFlag: "module.timekeeping.handover_plans" },
      // { to: "/people/timesheet/clerk-entry", label: "Clerk Entry", permission: "timekeeping.clerk_entry.initiate", featureFlag: "module.timekeeping.clerk_entry" },
      // { to: "/people/timesheet/reconciliation", label: "Reconciliation", permission: "timekeeping.reconciliation.manage", featureFlag: "module.timekeeping.reconciliation" },
    ],
  },
  {
    id: "people_admin",
    label: "People Admin",
    defaultRoute: "/people/timesheet/admin/balances",
    featureFlag: "module.people",
    items: [
      { subHeader: "Time & Attendance Admin" },
      { to: "/people/timesheet/admin/attendance", label: "Attendance", permission: "timekeeping.attendance.admin", permissionAny: TA_ATTENDANCE_SIDEBAR_PERMS, tooltip: "Colombia org calendar and today roster with PTO — Attendance Admin or Download Attendance in Roles", matchPaths: ["/people/timesheet/admin/attendance"] },
      { to: "/people/timesheet/admin/balances", label: "Leave", permission: "pto.admin", tooltip: "Leave balances, org approvals, and leave on behalf", matchPaths: ["/people/timesheet/admin/balances", "/people/timesheet/admin/approvals", "/people/timesheet/admin/leave"] },
      { to: "/people/timesheet/admin/timesheets", label: "Time", permission: "timekeeping.admin", permissionAny: TA_TIME_SIDEBAR_PERMS, resolveTo: timeHubSidebarPath, tooltip: "Timesheets, Timesheet Control, Timesheet Follow Up, HR/audit reports, charge codes, clerk entry, reconciliation", matchPaths: ["/people/timesheet/admin/timesheets", "/people/timesheet/admin/control", "/people/timesheet/admin/follow-up-emails", "/people/timesheet/admin/reports", "/people/timesheet/compliance", "/people/timesheet/charge-codes", "/people/timesheet/clerk-entry", "/people/timesheet/reconciliation"] },
      { to: "/people/timesheet/policies", label: "Admin Setup", permission: "timekeeping.admin", tooltip: "Policies, holidays, audit trail, approver delegations, Zoho sync", matchPaths: ["/people/timesheet/policies", "/people/timesheet/holidays", "/people/timesheet/admin/audit", "/people/timesheet/admin/approver-delegations", "/people/timesheet/zoho-leave"] },
      { subHeader: "CPM Admin" },
      { to: "/people/review-admin", label: "Review Admin", permission: "rro.admin", featureFlag: "module.cpm" },
      { to: "/people/reports", label: "Performance Reports", permission: "rro.admin", featureFlag: "module.cpm" },

      { to: "/admin/focus-compliance", label: "Focus Compliance", permission: "rro.admin", featureFlag: "module.cpm" },
    ],
  },
  // ── WORKFLOWS — hidden until module is ready ──────────────────────
  // {
  //   id: "workflows",
  //   label: "Workflows",
  //   defaultRoute: "/workflows/tasks",
  //   featureFlag: "module.workflows",
  //   items: [
  //     { to: "/workflows/tasks", label: "My Tasks", permission: "workflows.execute" },
  //     { to: "/workflows/templates", label: "Templates", permission: "workflows.view" },
  //     { to: "/workflows/runs", label: "Active Runs", permission: "workflows.view" },
  //     { to: "/workflows/schedules", label: "Schedules", permission: "workflows.view" },
  //     { to: "/workflows/reports", label: "Reports", permission: "workflows.view" },
  //     { to: "/workflows/agents", label: "Agent Performance", permission: "workflows.view" },
  //     { subHeader: "Reviews" },
  //     { to: "/people/aars", label: "After Action Reviews", permission: "workflows.view", featureFlag: "module.aar" },
  //     { to: "/workflows/ci-ideas", label: "CI Ideas", permission: "workflows.view" },
  //   ],
  // },
  // ── CONTENT — hidden until module is ready ────────────────────────
  // {
  //   id: "content",
  //   label: "Content",
  //   defaultRoute: "/content",
  //   featureFlag: "module.content_engine",
  //   items: [
  //     { to: "/content", label: "Content Dashboard", permission: "content.view" },
  //     { to: "/content/brief", label: "Content Brief", permission: "content.view" },
  //     { to: "/content/drafts", label: "Drafts", permission: "content.view" },
  //     { to: "/content/analytics", label: "Analytics", permission: "content.view" },
  //     { to: "/content/settings", label: "Settings", permission: "content.admin" },
  //   ],
  // },
  // {
  //   id: "agents",
  //   label: "Builder",
  //   defaultRoute: "/builder/processes",
  //   featureFlag: "module.agents",
  //   items: [
  //     { to: "/builder/processes", label: "Process Repo", permission: "builder.view", featureFlag: "module.process_repository" },
  //     // { to: "/builder/disposition", label: "Disposition Board", permission: "builder.view", featureFlag: "module.process_repository" },
  //     // { to: "/builder/discovery", label: "Discovery", permission: "builder.manage", featureFlag: "module.process_repository" },
  //     // { to: "/builder/compliance", label: "Compliance", permission: "builder.view", featureFlag: "module.process_repository" },
  //     // { to: "/builder/specs", label: "Specifications", permission: "builder.view" },
  //     { to: "/builder/actions", label: "Actions", permission: "builder.view", featureFlag: "module.scripted_actions" },
  //     { to: "/builder/tasks", label: "Task Registry", permission: "builder.view" },
  //     { to: "/builder/policy", label: "Policy Rules", permission: "admin.settings", featureFlag: "module.policy_gateway" },
  //     // { to: "/builder/audit", label: "Audit Trail", permission: "builder.audit", featureFlag: "module.audit_proof" },
  //     { to: "/agents", label: "Registry", permission: "admin.settings" },
  //     // { to: "/agents/new", label: "Build Agent", permission: "admin.settings", featureFlag: "module.agent_builder" },
  //     { to: "/agents/templates", label: "Templates", permission: "builder.view", featureFlag: "module.agent_builder" },
  //     { to: "/agents/runs", label: "Activity", permission: "admin.settings" },
  //   ],
  // },
  {
    id: "admin",
    label: "Admin",
    defaultRoute: "/ops/system",
    items: [
      { to: "/ops/system", label: "System Ops", permission: "system.ops_dashboard" },
      { to: "/admin/task-monitor", label: "Task Monitor", permission: "admin.settings" },
      { to: "/settings/platform", label: "Settings", permission: "admin.settings" },
      { to: "/settings/notifications", label: "Notifications", adminOnly: true },
      { to: "/settings/services", label: "Services", adminOnly: true },
      { to: "/settings/evolution", label: "Prompt Evolution", permission: "admin.settings" },
      { subHeader: "User Management" },
      { to: "/settings/users", label: "Users", permission: "admin.users" },
      { to: "/settings/roles", label: "Roles", permission: "admin.roles" },
      { subHeader: "Data" },
      { to: "/settings/data", label: "Data Admin", permission: "admin.data" },
      { to: "/settings/api-keys", label: "API Keys", permission: "admin.api_keys" },
      { to: "/settings/inbound-log", label: "Inbound Log", permission: "admin.data" },
      { to: "/crm/import", label: "Import", permission: "admin.data", featureFlag: "module.crm" },
      { to: "/sam/bulk", label: "SAM Daily Import", permission: "admin.data", featureFlag: "integration.sam_bulk" },
      { to: "/highergov/bulk", label: "HigherGov Daily Import", permission: "admin.data" },
      // { to: "/settings/feedback", label: "Feedback Triage", permission: "feedback.manage", featureFlag: "module.feedback" },
      // { to: "/activity", label: "Activity Log", permission: "admin.settings" },
      { to: "/activity/usage", label: "Usage Board", permission: "admin.settings" },
      // { to: "/admin/system-log", label: "System Log", permission: "system.ops_dashboard" },
    ],
  },
];


export const NAV_TOOLTIPS = {
  "/hub": {
    why: "Your AI-powered work assistant that knows Resolute",
    what: "Ask any work question — draft emails, research companies, analyze documents, plan your week",
    how: "Start a new chat or pick up where you left off. Select Sonnet (fast) or Opus (best quality)",
  },
  "/hub/weekly-focus": {
    why: "AI coaching for focus planning and quarterly objectives",
    what: "Structured coaching session: reflect, plan, compile and submit focus notes (weekly now, daily coming soon)",
    how: "Choose weekly or daily, chat with the coach, then compile and submit to your manager",
  },
  "/hub/weekly-focus/team": {
    why: "See how your team is doing",
    what: "Compiled focus notes from direct reports with priorities, blockers, and asks",
    how: "Review submitted notes, acknowledge, and provide feedback",
  },
  "/hub/templates": {
    why: "Reusable AI workflows for common tasks",
    what: "Library of structured prompts — draft capability statements, research companies, summarize meetings",
    how: "Pick a template, fill in the blanks, get results. Create and share your own",
  },
  "/hub/documents": {
    why: "Upload documents for AI-powered analysis",
    what: "Solicitations, contracts, compliance docs — ask questions, extract data, get summaries",
    how: "Upload a file, start a conversation about it",
  },
  "/leads": {
    why: "See all qualified leads in your pipeline",
    what: "Filterable list of all leads that passed automated screening",
    how: "Review, sort, and click through to lead details",
  },
  "/screening": {
    why: "Human review of AI-screened leads",
    what: "Triage queue for leads awaiting human go/no-bid decisions",
    how: "Review AI scores and rationale, then approve or pass each lead",
  },
  "/lead-triage": {
    why: "Primary lead review and disposition workflow",
    what: "Workflow-optimized triage with AI recommendations, batch actions, three-tier information access, and calibration feedback",
    how: "Review leads by priority tabs, disposition with one click, use tooltip/popup for deeper analysis",
  },
  "/triage": {
    why: "Unified lead triage and disposition workflow",
    what: "Score-filtered tabs, lead summaries, keyboard shortcuts, inline disposition, batch actions, team status, and enrichment tracking",
    how: "Review pre-enriched leads, use keyboard shortcuts (G=go, P=pass, D=disposition), batch accept/pass selected leads",
  },
  "/opportunities": {
    why: "Track dispositioned opportunities through capture",
    what: "CRM pipeline view of opportunities in various stages",
    how: "View pipeline, manage tasks, track contacts and organizations",
  },
  "/crm/contacts": {
    why: "Build and maintain your contact network",
    what: "Directory of contacts across agencies, primes, and partners",
    how: "Search, add, and link contacts to opportunities and organizations",
  },
  "/crm/organizations": {
    why: "Track agencies, primes, and teaming partners",
    what: "Organization profiles with contacts, contracts, and relationship history",
    how: "Search, add, and view organization details and linked opportunities",
  },
  "/crm/pulse": {
    why: "Spot deals that need attention before they slip",
    what: "Deal health scoring across freshness, deadline, tasks, completeness, and momentum",
    how: "Review the attention queue sorted worst-first, filter by risk level, click through to fix issues",
  },
  "/past-performance": {
    why: "Leverage past wins for future proposals",
    what: "Contract past performance records with CPARS-style ratings",
    how: "Add, search, and reference past performance in proposals",
  },
  "/people": {
    why: "Know your team and their capabilities",
    what: "Workforce directory with skills, clearances, and assignments",
    how: "Search, filter, and view detailed personnel profiles",
  },
  "/people/certifications": {
    why: "Know who holds which certifications and language skills",
    what: "Admin-only directory of employee certifications and language fluency",
    how: "Click an employee to upload certifications or add language skills",
  },
  "/people/rro": {
    why: "Align individual work with company strategy",
    what: "Roles, Responsibilities, and Objectives for each team member",
    how: "Define strategic/operational/admin responsibilities with measurable objectives",
  },
  "/people/actionables": {
    why: "Start each day with clear priorities",
    what: "Auto-generated daily action items from tasks, leads, and objectives",
    how: "Review, complete, or refresh your daily actionable list",
  },
  "/people/aars": {
    why: "Capture lessons learned from key events",
    what: "Standard after action review form for separations, onboarding, incidents, requisition closes, and more",
    how: "Create AARs manually or let workflows auto-generate them. Review, complete, and track action items",
  },
  "/people/priorities": {
    why: "Cascade company strategy into individual objectives",
    what: "Company-level strategic priorities with KPIs, owners, and fiscal alignment",
    how: "Review active priorities, create new ones, link to individual RRO objectives",
  },
  "/people/checkin": {
    why: "Monthly progress tracking (status-based, not scored) and the quarterly self-assessment",
    what: "Check-In tab (months 1 and 2): update objective status. Self-Assessment tab: score your own objectives 1-5 with evidence and justification. My Evaluation tab: your manager's evaluation (Colombia: acknowledge it)",
    how: "Pick the quarter, then a tab. In Check-In, select the month, update each objective status and add notes. In Self-Assessment, score each objective, provide evidence, and submit for manager review.",
  },
  "/people/team-reviews": {
    why: "Leaders close out each report's monthly check-in and evaluate them each quarter",
    what: "Opens on whichever tab has its window open today. Check-Ins: submission status, 1:1 date, review notes and acknowledge state. Evaluations: scoring, validation meeting and PDF",
    how: "Check-Ins: pick the month, open a report, add the 1:1 date and notes, Send & Acknowledge. Evaluations: score, submit, then set the validation meeting so the employee sees it.",
  },
  "/people/evaluations": {
    why: "Evaluate direct reports on objectives and professional behaviors",
    what: "Score objectives (70% weight) and 7 professional behaviors (30% weight). Total = (0.70 × RRO) + (0.30 × Behaviors)",
    how: "Select cycle, choose team member, score objectives and behaviors, calculate tier, submit",
  },
  "/people/evaluations/ranking": {
    why: "Calibrate performance across the organization",
    what: "Forced ranking with distribution constraints: max 20% A, max 50% A+B, ≥50% at or below Meets",
    how: "Review scores, assign rank tiers, validate distribution, apply rankings",
  },
  "/people/review-admin": {
    why: "Manage the quarterly performance cycle timeline",
    what: "Planning cycles (T-30 → T-0 cascade) and review cycles (planning → finalized)",
    how: "Create cycles, set dates, advance status, monitor progress",
  },
  "/people/reports": {
    why: "Performance analytics and accountability tracking",
    what: "Tier distribution, PAP tracking, exceptional adjustments, manager compliance",
    how: "Review dashboards, monitor at-risk personnel, track PAP progress",
  },
  "/people/my-pap": {
    why: "Give the team member a direct way to work their own Performance Acceleration Plan without going through HR's admin-only reports",
    what: "The PAP where you're the team member the plan is for",
    how: "Open it to log milestone progress or accept the plan as team member",
  },
  "/people/team-paps": {
    why: "Give the direct manager a direct way to run a report's Performance Acceleration Plan without going through HR's admin-only reports",
    what: "PAPs you're running as the direct manager — your reports' plans, not your own",
    how: "Open a PAP to log gaps, behaviors, and milestone progress, or accept the plan as leader",
  },
  "/intel/stream": {
    why: "Stay informed about market movements",
    what: "Real-time feed of intelligence from all configured sources",
    how: "Scan, filter, and drill into intelligence items",
  },
  "/intel/brief": {
    why: "Impact-organized intelligence for decision-making",
    what: "Live morning brief with critical patterns, high-impact signals, and per-agency/opportunity intelligence",
    how: "Review sections, drill into agencies, check constellation graphs for key opportunities",
  },
  "/intel/brief-v2": {
    why: "Five-section standup-friendly intel — what's happening at our customers, Competi-Mates, OEMs, in our market, and in LATAM",
    what: "Daily compiled brief with fixed sections and Teams-readable text sizing — admin-managed Competi-Mate + OEM rosters",
    how: "Review the brief, browse archive, switch to Manage Lists tab to curate (admin only)",
  },
  "/intel/brief/archive": {
    why: "Review historical compiled briefs",
    what: "Archive of daily compiled briefs with 7-section watchlist categorization",
    how: "Browse by date, read narratives, check delivery status",
  },
  "/intel/key-talent": {
    why: "Track personnel movements across the market",
    what: "AI-identified staffing leads and key talent movements",
    how: "Review talent signals, flag for recruitment or teaming",
  },
  "/staffing": {
    why: "Turn contract awards into staffing pipeline",
    what: "Key Talent Pipeline — companies winning federal contracts that need Resolute talent (SAP, RPA, AI/ML, Qlik)",
    how: "Review AI-scored leads, draft outreach, track engagement, convert to opportunities",
  },
  "/staffing/admin": {
    why: "Fine-tune what the Key Talent scanner looks for",
    what: "Configure screening criteria, scoring weights, keyword filters, agency focus, and automation settings. Save and switch between named config presets.",
    how: "Adjust settings, save as preset, activate different presets with one click",
  },
  "/intel/actions": {
    why: "Turn intelligence into action",
    what: "AI-recommended actions based on intelligence analysis",
    how: "Review recommendations, accept or dismiss, assign to team",
  },
  "/intel/search": {
    why: "Find specific intelligence quickly",
    what: "Full-text search across all intelligence records",
    how: "Search by keyword, filter by source, date, or category",
  },
  "/content/brief": {
    why: "Daily intelligence and content planning in one view",
    what: "Morning Brief with flash items, intel digest, market pulse, and LinkedIn content options",
    how: "Review brief, select a content option, generate draft, provide calibration feedback",
  },
  "/content/drafts": {
    why: "Manage LinkedIn post drafts from idea to publication",
    what: "Edit AI-generated drafts, copy to clipboard, paste published text back for voice calibration",
    how: "Edit draft, publish to LinkedIn, paste final text, add engagement data after 24 hours",
  },
  "/content/analytics": {
    why: "Track content performance and calibration patterns",
    what: "Pillar distribution, engagement by format, voice delta trends, objective coverage",
    how: "Review charts, identify high-performing pillars and formats, adjust strategy",
  },
  "/content/settings": {
    why: "Configure brand voice and content strategy",
    what: "Brand profile, pillar weights, voice settings, priority topics, voice samples",
    how: "Edit brand profile fields, adjust pillar weights, add voice samples",
  },
  "/upload": {
    why: "Manually ingest solicitations for evaluation",
    what: "Upload PDF/DOCX files or paste text for AI screening",
    how: "Drag and drop files or paste solicitation text",
  },
  "/sam": {
    why: "Discover new federal opportunities",
    what: "Search SAM.gov for active solicitations and notices",
    how: "Enter keywords, filter results, and ingest selected opportunities",
  },
  "/settings": {
    why: "Configure AI models, thresholds, and system behavior",
    what: "Runtime settings for all AI tiers and scoring parameters",
    how: "Adjust model selections, scoring weights, and prompt overrides",
  },
  "/ops/system": {
    why: "Monitor system health and costs",
    what: "System operations dashboard with collection health, alarms, and cost tracking",
    how: "Review metrics, acknowledge alarms, track inference costs",
  },
  "/agents": {
    why: "Govern and monitor all AI agents in the system",
    what: "Registry of registered agents with trust levels, circuit breakers, and execution history",
    how: "View agents, adjust trust levels, monitor runs, reset circuit breakers",
  },
  "/agents/new": {
    why: "Create new AI agents without writing code",
    what: "Step-by-step wizard to build domain, persona, process, or task agents from templates",
    how: "Choose an archetype, configure identity and prompts, set capabilities, then create",
  },
  "/agents/templates": {
    why: "Jumpstart agent creation with pre-built templates",
    what: "Gallery of system and custom templates for common agent archetypes",
    how: "Browse templates by category, preview configs, then use one to build a new agent",
  },
  "/agents/runs": {
    why: "Live view of background processing and agent executions",
    what: "Background activity feed (collection, enrichment, scoring, briefs) plus formal agent run history",
    how: "Watch live activity with auto-refresh, filter by task type, switch to Agent Runs tab for formal runs",
  },
  "/contract-vehicles": {
    why: "Track contract vehicles and compliance deadlines",
    what: "IDIQ, GWAC, BPA, and MAC vehicles with documents and compliance calendars",
    how: "View vehicles, upload documents, monitor compliance events",
  },
  "/contract-vehicles/shield": {
    why: "Manage the MDA SHIELD IDIQ end-to-end",
    what: "SHIELD vehicle details, documents, compliance events, and AI agent activity",
    how: "Upload ordering guides, track deadlines, review SHIELD agent briefs",
  },
  "/builder/processes": {
    why: "Catalog and prioritize every business process",
    what: "Central repository of all business processes with discovery, scoring, and lifecycle tracking",
    how: "Browse processes, score them, set dispositions, link to agent specs",
  },
  "/builder/disposition": {
    why: "Visualize process pipeline at a glance",
    what: "Kanban board showing processes across disposition stages",
    how: "Drag cards between columns to advance disposition, click for details",
  },
  "/builder/discovery": {
    why: "Structured process discovery and documentation",
    what: "Discovery sessions for interviewing stakeholders and documenting processes",
    how: "Create sessions, take notes, link discovered processes",
  },
  "/builder/compliance": {
    why: "Map processes to compliance frameworks",
    what: "Crosswalk matrix showing process coverage across ISO 9001, CMMC, and NIST controls",
    how: "Review coverage, identify gaps, drill into specific controls",
  },
  "/builder/specs": {
    why: "Design agent specifications before building",
    what: "Agent specification editor with purpose, tools, boundaries, and prompt sections",
    how: "Create specs, define capabilities, set trust levels, then deploy as agents",
  },
  "/builder/actions": {
    why: "Execute bounded operations with one click",
    what: "Library of scripted actions — deterministic, LLM-assisted, or multi-step",
    how: "Browse actions, execute with parameters, review results. Also accessible via Ctrl+K",
  },
  "/builder/tasks": {
    why: "See every kind of task the system can create — and what's happening with each",
    what: "Visible catalog of task types grouped by module, with live counts and lifecycle history per instance",
    how: "Click any card to see recent instances, who they were assigned to, and whether they were completed or dismissed (with reason)",
  },
  "/builder/policy": {
    why: "Govern what agents can and cannot do",
    what: "Database-backed policy rules evaluated on every agent action",
    how: "Create rules by scope (agent, tool, module), set actions (permit/deny/queue), manage priority",
  },
  "/workflows/tasks": {
    why: "Your pending workflow steps in one place",
    what: "Open tasks assigned to you across all active workflows, plus agent outputs awaiting your review",
    how: "Complete steps with field data, approve/reject agent outputs, track overdue items",
  },
  "/workflows/templates": {
    why: "Standardized, repeatable business processes",
    what: "Library of workflow templates covering HR, Finance, IT, Compliance, and CPM/RRO with configurable steps and agent automation",
    how: "Browse templates, create new ones, configure steps with fields, assign agents, publish for use",
  },
  "/workflows/runs": {
    why: "Track all active and completed workflow executions",
    what: "Real-time view of workflow runs with progress tracking, overdue detection, and status filtering",
    how: "Monitor progress, click into runs for step-by-step detail, cancel stuck workflows",
  },
  "/workflows/schedules": {
    why: "Automate recurring workflow launches",
    what: "Schedule workflows to run on cron patterns, one-time dates, or event triggers",
    how: "Create schedules, pause/resume, trigger immediately, monitor next run times",
  },
  "/workflows/reports": {
    why: "Measure process discipline across the organization",
    what: "Composite process discipline score from completion rates, on-time performance, escalations, and agent interventions",
    how: "Review metrics, drill into completion and on-time reports, identify bottleneck templates",
  },
  "/workflows/agents": {
    why: "Monitor AI agent performance within workflows",
    what: "Trust levels, approval rates, edit distances, circuit breaker states for workflow-assigned agents",
    how: "Review agent metrics, identify agents needing trust adjustments or retraining",
  },
  "/ops/projects": {
    why: "Manage all revenue-generating projects and assignments",
    what: "Project list with sell/cost rates, headcount, margins, and POP tracking",
    how: "Create projects, add assignments, record actuals, track against funded value",
  },
  "/ops/forecast": {
    why: "Project revenue forward with confidence weighting",
    what: "12-month rolling forecast with booked vs pipeline breakdown and per-project drill-down",
    how: "View monthly projections, toggle pipeline visibility, save snapshots for comparison",
  },
  "/ops/allocation": {
    why: "Ensure people are properly utilized across projects",
    what: "Cross-project allocation report showing over/under-allocated personnel",
    how: "Review allocation percentages, identify gaps, rebalance assignments",
  },
  "/ops/invoices": {
    why: "Track invoicing and accounts receivable",
    what: "Invoice management with creation, approval, aging, and payment tracking",
    how: "Create invoices from project actuals, track payment status, monitor AR aging",
  },
  "/ops/schedule": {
    why: "Plan and track project delivery milestones",
    what: "Delivery schedule with project timelines, milestones, and dependencies",
    how: "View project schedules, track milestones, monitor delivery health",
  },
  "/ops/dashboard": {
    why: "Executive view of revenue, margins, and staffing health",
    what: "Operations dashboard with KPI cards, planning status, top/bottom profitability, at-risk revenue, and staffing alerts",
    how: "Review metrics at a glance, drill into projects or workforce planning, monitor staffing health alerts",
  },
  "/ops/workforce": {
    why: "Plan staffing demand and match supply to projects",
    what: "Workforce planning grid — billets (demand) vs people (supply) with month-by-month hours, sell rates, and margin calculations",
    how: "Create billets per project, assign people, plan monthly hours, review revenue/cost/margin projections",
  },
  "/ops/profitability": {
    why: "Understand margin health across all projects",
    what: "Profitability drill-down showing revenue, cost, and margin for every active project with planning status indicators",
    how: "Sort by margin %, identify under-performing projects, drill into workforce grid for adjustments",
  },
  "/builder/audit": {
    why: "Complete audit trail for every system action",
    what: "Provenance chain for agent actions, policy evaluations, trust changes, and process transitions",
    how: "Search by actor, entity, module, or compliance framework. Export compliance reports",
  },
  "/settings/corpus": {
    why: "Give every AI agent Resolute's voice and values",
    what: "Upload foundational documents (ROC, CPM, capabilities) that get chunked, embedded, and injected into agent system prompts",
    how: "Upload documents, assign tags, activate for agents. Configure per-agent corpus injection (tags, tokens, mode)",
  },
  "/settings/api-keys": {
    why: "Give a bot or a person a key limited to specific resources",
    what: "Generate, edit, rotate, and revoke API keys. A bot key pushes intake for one source. A person key is limited to the resources you check.",
    how: "Pick Bot or Person, choose the source or the resources, generate the key, and copy it once",
  },
  "/settings/inbound-log": {
    why: "Monitor external data flowing into the pipeline",
    what: "Every inbound API request logged with status, timing, source, and outcome (created, duplicate, error)",
    how: "Filter by source, status, or key. Check stats for volume trends and error rates",
  },
  "/settings/platform": {
    why: "Fine-tune runtime behavior without redeploying",
    what: "Feature flags, cost budgets, and model configurations for every AI tier",
    how: "Toggle features, set spending limits, change model providers per tier",
  },
  "/settings/evolution": {
    why: "All active AI prompts in one place with full version history",
    what: "Prompt library — browse by category, view and edit system + user prompts, see which model each version targets, diff against prior versions, promote or roll back",
    how: "Pick a prompt, edit as a new draft, set target model, promote when ready. Rollback reactivates the parent version.",
  },
  "/settings/users": {
    why: "Manage who can access the system",
    what: "User accounts with role assignments, status, and invitation management",
    how: "Invite users, assign roles, activate/deactivate accounts",
  },
  "/settings/roles": {
    why: "Define what each role can do",
    what: "Custom RBAC roles with granular permission assignments across all modules",
    how: "Create roles, toggle permissions, assign to users",
  },
  "/settings/data": {
    why: "Maintain database health and manage bulk operations",
    what: "Data administration tools for cleanup, export, and bulk operations",
    how: "Run data maintenance tasks, export records, manage system data",
  },
  "/crm/import": {
    why: "Bring existing CRM data into the system",
    what: "Import contacts, organizations, and opportunities from CSV or external CRM",
    how: "Upload CSV, map columns, preview, and import",
  },
  "/sam/bulk": {
    why: "Automated daily ingestion of federal opportunities",
    what: "SAM.gov daily bulk CSV download and processing with multi-key rotation",
    how: "Monitor daily runs, review ingestion stats, troubleshoot failures",
  },
  "/highergov/bulk": {
    why: "Second independent feed of the same federal notices, so a gap in SAM does not become a missed lead",
    what: "Nightly HigherGov opportunity API pull, filtered by the same NAICS, PSC and keyword rules as SAM",
    how: "Run it on demand, watch the funnel, and review what the filter dropped to tune the lists",
  },
  "/activity/usage": {
    why: "Know who's actually using the app and when, and whether failures line up with heavy usage",
    what: "Health banner, active-users-right-now count, peak-hours chart, DB and resource trends, activity log",
    how: "Read the banner first, then switch tabs for system resources or the full activity log",
  },
  "/activity": {
    why: "See everything that happened in the system",
    what: "Chronological log of all user and system actions with filtering",
    how: "Filter by user, action type, or entity. Click through to related records",
  },
  "/admin/system-log": {
    why: "Diagnose system issues with unified log access",
    what: "Inference calls, ingestion runs, system errors, and alarm history in one view",
    how: "Switch tabs to view LLM calls, ingestion health, errors, or full alarm audit trail",
  },
  "/coord/tasks": {
    why: "Stay on top of your assigned coordination tasks",
    what: "Personal task queue from coordination chains, follow-ups, and team assignments",
    how: "View, complete, and manage tasks assigned to you across all opportunities",
  },
  "/proactive": {
    why: "AI-discovered opportunities before they hit SAM.gov",
    what: "Proactive lead discovery agent scanning forecasts, news, and market signals",
    how: "Review AI-surfaced leads, promote to pipeline, or dismiss",
  },
  "/forecast": {
    why: "Anticipate federal spending before solicitations drop",
    what: "Agency procurement forecasts with Resolute capability matching",
    how: "Browse forecasts by agency, filter by NAICS or technology match",
  },
  "/intel/dashboard": {
    why: "Know at a glance whether Intel is working and whether today's brief is complete",
    what: "Morning Brief section status, news processing health, failing or silent sources, alerts, 14-day trend",
    how: "Check the banner first; open any count to see which sources are affected",
  },
  "/intel/admin": {
    why: "Configure intelligence collection sources and behavior",
    what: "OSINT source management, collection schedules, PIR configuration",
    how: "Add sources, set collection frequency, define priority information requirements",
  },
  "/intel/discovery": {
    why: "Proactive intelligence before solicitations drop",
    what: "Discovery Scout scans FPDS awards, SAM.gov forecasts, and FSRS subawards for recompete windows, teaming signals, and competitor movements",
    how: "Review scored discoveries, promote high-value findings to Intel Lake, or dismiss false positives",
  },
  "/intel/discovery/admin": {
    why: "Monitor and configure Discovery Scout scanners",
    what: "Scanner health status, run history, configuration for scan schedules and scoring thresholds",
    how: "Toggle scanners, trigger manual scans, adjust relevance thresholds",
  },
};
